/**
 * The ticket queue: tickets appear, tickets run out, and a serve fills one.
 *
 * Two properties shape this module, and both come from the determinism
 * assertion rather than from the game design.
 *
 * **Nothing is random and nothing is counted.** Which ticket appears when is a
 * pure function of the tick index: a spawn slot is `firstOrderDelay + k *
 * interval`, its id is derived from `k`, and its dish is `orderPool[k % n]`.
 * That means `GameState` needs no spawn cursor and no id counter, and therefore
 * `applyEvent` needs no way to rebuild one — the fold sees `ORDER_SPAWNED` and
 * appends, and the two halves stay identical for free. A `Math.random()` here
 * would have broken replay on the first ticket.
 *
 * **The cap skips a slot rather than delaying it.** When the queue is already
 * at `maxActiveOrders` the slot passes unused; the next ticket arrives on the
 * regular cadence instead of bunching up behind the backlog. A queue that
 * catches up after a bad patch punishes the players twice for the same mistake.
 *
 * **A ticket's fuse is measured against its own dish.** `expiresAtTick` is the
 * dish's cook time plus a slack that shrinks by slot to a floor, so the round
 * tightens without ever issuing a ticket nobody could have filled. The slot
 * index is the only input to the shrink, which keeps the whole function pure.
 *
 * **A map may say when instead.** A `// schedule:` directive replaces the
 * cadence with a timetable: entry `i` spawns on its own tick as `order-i`,
 * several may share a tick, and the fuse is the same formula with the entry
 * index for the slot. Still a pure function of the map, so all of the above
 * holds; `validateMap` additionally keeps a schedule from ever meeting the cap,
 * so on a valid map every entry spawns whatever the players do.
 */

import type { GameEvent, IngredientId, Order, ScheduledOrder } from '@overcooked/shared'
import { msToTicks } from '@overcooked/shared'
import type { Content, DishDef } from './content'
import { durationTicks } from './rules'
import { scoreForExpiry } from './scoring'
import type { GameState } from './state'

/** Spawn cadence in ticks. At least one, so a tiny interval cannot divide by zero. */
export function spawnIntervalTicks(content: Content): number {
  return Math.max(1, msToTicks(content.timings.orderSpawnIntervalMs))
}

export function firstSpawnTick(content: Content): number {
  return Math.max(0, msToTicks(content.timings.firstOrderDelayMs))
}

export function dishFor(content: Content, dishId: string): DishDef | undefined {
  return content.recipes.dishes.find((d) => d.id === dishId)
}

/**
 * What this dish costs in stove time, in ticks.
 *
 * One cook per cooked item, because the pan holds one thing at a time — so a
 * stew is three of them and fried meat is one. Anything on the plate that no
 * `cookable` row produces (a raw garnish, were content to ship one) costs
 * nothing, which is the honest answer rather than a guess.
 *
 * It is a sum rather than a max on purpose: two pans let two ingredients cook
 * in parallel, but a single chef cannot be at both, and the budget is written
 * for the kitchen that has one stove free.
 */
export function dishCookTicks(content: Content, dishId: string): number {
  const dish = dishFor(content, dishId)
  if (!dish) return 0
  let total = 0
  for (const id of dish.contents) {
    const row = content.recipes.cookable.find((c) => c.to === id)
    if (row) total += durationTicks(content, row.toDoneKey)
  }
  return total
}

/**
 * How long slot `k`'s ticket lives, in ticks.
 *
 * The budget is the dish's own cook time plus slack, and the slack shrinks by
 * slot until it hits its floor. Expressed relative to the dish because an
 * absolute window cannot be right for both a one-cook dish and a three-cook
 * one; decaying by slot index rather than by elapsed time because this has to
 * stay a pure function of the slot for a replay to rebuild the same queue.
 */
export function slackTicksForSlot(content: Content, slot: number): number {
  const { orderSlackMs, orderSlackDecayMs, minOrderSlackMs } = content.timings
  const ms = Math.max(minOrderSlackMs, orderSlackMs - slot * orderSlackDecayMs)
  return msToTicks(ms)
}

export function validityTicksForSlot(content: Content, slot: number, dishId: string): number {
  return Math.max(1, dishCookTicks(content, dishId) + slackTicksForSlot(content, slot))
}

/**
 * Which spawn slot this tick is, or `null` if it is not one. Slot 0 is the
 * first ticket of the round.
 */
export function spawnSlotAt(content: Content, atTick: number): number | null {
  const first = firstSpawnTick(content)
  if (atTick < first) return null
  const interval = spawnIntervalTicks(content)
  const since = atTick - first
  if (since % interval !== 0) return null
  return since / interval
}

/** The ticket slot `k` would produce. Deterministic, and free of state. */
export function orderForSlot(
  content: Content,
  slot: number,
  atTick: number,
  pool: string[] = content.recipes.orderPool,
): Order {
  const dishId = pool[slot % pool.length]
  return {
    id: `order-${slot}`,
    dishId,
    spawnedAtTick: atTick,
    expiresAtTick: atTick + validityTicksForSlot(content, slot, dishId),
  }
}

/**
 * The ticket a map's `// schedule:` entry `index` produces.
 *
 * Its id is `order-<index>` — the index after sorting, so two entries on the
 * same tick (even for the same dish) are two tickets — and its fuse is the
 * same formula as a cadence slot's, with the entry index standing in for the
 * slot. Two same-dish tickets at one tick therefore lapse at different ticks.
 */
export function scheduledOrder(content: Content, entry: ScheduledOrder, index: number): Order {
  return {
    id: `order-${index}`,
    dishId: entry.dishId,
    spawnedAtTick: entry.atTick,
    expiresAtTick: entry.atTick + validityTicksForSlot(content, index, entry.dishId),
  }
}

/**
 * Every ticket a round would issue if the rail never filled, in spawn order:
 * the schedule's entries when there is one, else one per cadence slot before
 * `roundTicks`. What a planner can know about the future — on a map with a
 * valid schedule it is exactly what will spawn, because validation keeps the
 * rail from ever filling.
 */
export function issuedOrders(
  content: Content,
  source: { orderPool: string[]; orderSchedule?: ScheduledOrder[] },
  roundTicks: number,
): Order[] {
  const schedule = source.orderSchedule ?? []
  if (schedule.length > 0) {
    return schedule
      .map((entry, index) => scheduledOrder(content, entry, index))
      .filter((order) => order.spawnedAtTick < roundTicks)
  }
  const pool = source.orderPool.length > 0 ? source.orderPool : content.recipes.orderPool
  const first = firstSpawnTick(content)
  const interval = spawnIntervalTicks(content)
  const out: Order[] = []
  for (let slot = 0; first + slot * interval < roundTicks; slot += 1) {
    out.push(orderForSlot(content, slot, first + slot * interval, pool))
  }
  return out
}

/**
 * Everything the queue does on its own, run after the timers and before the
 * tick index moves on.
 *
 * Expiry is settled before a spawn is considered, so the seat a lapsed ticket
 * frees is available to the ticket arriving on the same tick. The alternative
 * skips a slot the players never actually blocked.
 */
export function advanceOrders(state: GameState, events: GameEvent[], content: Content): void {
  expireOrders(state, events, content)
  spawnOrder(state, events, content)
}

function expireOrders(state: GameState, events: GameEvent[], content: Content): void {
  if (state.orders.length === 0) return

  const survivors: Order[] = []
  for (const order of state.orders) {
    if (state.tick < order.expiresAtTick) {
      survivors.push(order)
      continue
    }
    const scoreDelta = scoreForExpiry(content)
    state.score += scoreDelta
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: 'system',
      type: 'ORDER_EXPIRED',
      orderId: order.id,
      scoreDelta,
    })
  }
  state.orders = survivors
}

function spawnOrder(state: GameState, events: GameEvent[], content: Content): void {
  // A map with a schedule issues exactly its entries, several on one tick if
  // it says so, and no cadence tickets at all. A state without one (every map
  // that has none, and every keyframe recorded before schedules existed) runs
  // the cadence below, unchanged.
  const schedule = state.orderSchedule
  if (schedule && schedule.length > 0) {
    schedule.forEach((entry, index) => {
      if (entry.atTick !== state.tick) return
      // The same rule as the cadence. Validation keeps a map's schedule from
      // ever reaching it; a state built by hand still gets today's behaviour.
      if (state.orders.length >= content.timings.maxActiveOrders) return
      issue(state, events, scheduledOrder(content, entry, index))
    })
    return
  }

  const slot = spawnSlotAt(content, state.tick)
  if (slot === null) return
  // The queue is full: this slot passes, and the cadence carries on unchanged.
  if (state.orders.length >= content.timings.maxActiveOrders) return

  // The pool is the round's, not the file's: a 5x5 kitchen cannot cook a roast
  // platter, and the map says so.
  const pool = state.orderPool.length > 0 ? state.orderPool : content.recipes.orderPool
  issue(state, events, orderForSlot(content, slot, state.tick, pool))
}

function issue(state: GameState, events: GameEvent[], order: Order): void {
  state.orders = [...state.orders, order]
  events.push({
    tick: state.tick,
    wallClock: 0,
    actor: 'system',
    type: 'ORDER_SPAWNED',
    order: { ...order },
  })
}

// ---------------------------------------------------------------------------
// Matching a served plate against the queue
// ---------------------------------------------------------------------------

/**
 * Whether a plate holds exactly what a dish asks for. A multiset comparison,
 * not a sequence one: the order ingredients go onto the plate must not matter,
 * because two players filling one plate cannot be asked to agree on a sequence
 * over voice.
 */
export function sameContents(a: IngredientId[], b: IngredientId[]): boolean {
  if (a.length !== b.length) return false
  const left = [...a].sort()
  const right = [...b].sort()
  return left.every((id, i) => id === right[i])
}

/**
 * If two tickets want the same dish, the one closest to expiry is filled
 * first. Ties break on id, which is stable across a replay because
 * ids are derived from the spawn slot rather than issued by a counter.
 *
 * Returning `null` is a legal outcome, not a failure — a plate holding
 * something nobody ordered serves and scores nothing.
 */
export function matchOrder(state: GameState, contents: IngredientId[], content: Content): Order | null {
  let best: Order | null = null
  for (const order of state.orders) {
    const dish = dishFor(content, order.dishId)
    if (!dish || !sameContents(dish.contents, contents)) continue
    if (
      best === null ||
      order.expiresAtTick < best.expiresAtTick ||
      (order.expiresAtTick === best.expiresAtTick && order.id < best.id)
    ) {
      best = order
    }
  }
  return best
}

/** Take a filled ticket out of the queue. The `SERVED` event does the same fold. */
export function removeOrder(state: GameState, orderId: string): void {
  state.orders = state.orders.filter((o) => o.id !== orderId)
}
